import React, { useState } from "react";
import { Popover, OverlayTrigger } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheckCircle, faCircle, faInfo, faCompressArrowsAlt, faExpandArrowsAlt, faExclamationTriangle } from "@fortawesome/free-solid-svg-icons";
import BellIcon from "images/bell-on-light.svg";
import InteractIcon from "images/InteractIcon.svg";
import { Button, Icon } from "aible";
import { useDispatch, useSelector } from "types/store";
import { isEnabled } from "utils/featureFlags";
import { getOutputContext, builder } from "utils/relation";
import { col, ifThen, scalar } from "utils/expression";
import { LineInput } from "components/Inputs/LineInput";
import Tooltip from "components/Inputs/Tooltip";
import LoadingIndicator from "components/LoadingIndicator/LoadingIndicator";
import SensePieChart from "components/Tiles/Sense/SensePieChart";
import ProblematicPredictors from "components/CreateWizard/Academic/ProblematicPredictors";
import { getDataset, MAX_USEFUL_VARIABLES } from "containers/Home/reducer";
import { AI_READINESS_BREAKPOINT, AI_READINESS_PREDICTOR_WARNING, getAiReadyMetrics, getCorrelationMetricsFromDataset, HIGH_CORRELATION_CUTOFF } from "containers/Setup/reducer";
import { TRANSFORM_TYPE_MAP } from "components/Dataset/Transform";
import { getBaseDraft, rebaseDraft, submitDraft } from "containers/Relation/reducer";
import styles from "./style.module.css";
import { DatePart } from "types/expression";
import { getIsOpenAiReadiness } from "containers/Sense/dataset-selectors";
import { updateTileState } from "containers/Sense/reducer";
const getAiReadinessMessage = (dataset, aiReadyMetrics) => {
    const outcome = dataset?.outcome ?? "the outcome's";
    const predictors = aiReadyMetrics?.aiReady;
    const aiStatus = aiReadyMetrics?.aiReadinessStatus;
    const isLowPredictors = (aiReadyMetrics?.aiReady?.length || 0) + (aiReadyMetrics?.secondOrder?.length || 0) <=
        AI_READINESS_PREDICTOR_WARNING.count;
    switch (aiStatus) {
        case "NO_SIGNAL":
            return "Please Collect More Data";
        case "LOW_SIGNAL":
            if (predictors?.length >= AI_READINESS_PREDICTOR_WARNING.count) {
                let lowPredictorsCount = 0;
                predictors?.forEach((p) => {
                    if (p.value < AI_READINESS_PREDICTOR_WARNING.value) {
                        lowPredictorsCount++;
                    }
                });
                if (lowPredictorsCount >= AI_READINESS_PREDICTOR_WARNING.count) {
                    return "Data Readiness Low, but still better than guessing. Proceed with caution. Many weak predictors found. More data may help.";
                }
            }
            return "Data Readiness Low, but still better than guessing. Proceed with caution. More data may help.";
        case "HIGH_SIGNAL":
            if (isLowPredictors) {
                return `Data Readiness Passed, but adding more variables will be helpful.`;
            }
            return "Data Readiness Passed";
        case "SUSPECT_SIGNAL":
            return `Data Readiness may be too high. Remove fields that are known only after ${outcome} status has been decided.`;
        default:
            return "No Data Readiness Metrics Found";
    }
};
export const ColumnIgnoredWarning = ({ dataset }) => {
    const base = useSelector((state) => getBaseDraft(state, dataset.id));
    const baseContext = getOutputContext(base);
    const columns = dataset?.labels ?? [];
    const columnCount = columns.length;
    const ignoredCount = columnCount - Object.keys(baseContext).length;
    const showWarning = columnCount - ignoredCount <= 2 && dataset?.status_name === "DONE";
    if (!showWarning) {
        return React.createElement(React.Fragment, null);
    }
    return (React.createElement("div", { className: styles.ignoredColumnsWarningWrapper },
        React.createElement("div", null,
            React.createElement(FontAwesomeIcon, { icon: faExclamationTriangle, style: { color: "#F8CD7E" } })),
        React.createElement("div", { style: { marginLeft: "10px" } }, `You are excluding almost all of the data (${ignoredCount} of ${columnCount} columns). This may cause an error when evaluating your dataset.`)));
};
export const DataReadinessTooltip = ({ dataset, isOnlyInfoCircle }) => {
    const outcome = dataset?.outcome;
    return (React.createElement("div", { className: !isOnlyInfoCircle ? styles.aireadinessTitle : "" },
        isOnlyInfoCircle !== true && React.createElement("div", null, "Data Readiness"),
        React.createElement("div", { onClick: (e) => e.stopPropagation() },
            React.createElement(OverlayTrigger, { placement: "top", trigger: ["hover"], rootClose: true, overlay: React.createElement(Popover, { id: "popover-positioned-top", title: React.createElement("strong", { style: { fontSize: "13px" } }, "What Does Data Readiness Mean?") },
                    React.createElement("div", { className: styles.aiReadinessOverlay },
                        React.createElement("div", { className: styles.aiReadinessBody },
                            React.createElement("strong", null, "If you are analyzing data, "),
                            React.createElement("span", null, `the data readiness indicates whether charts that you draw are likely to be statistically sound. For example, a chart may look visually very interesting because the average ${outcome ?? "outcome"} is significantly different, but if the standard deviation is significantly greater than the difference in averages, the chart might mislead you.`)),
                        React.createElement("div", { className: styles.aiReadinessBody },
                            React.createElement("strong", null, "If you are creating a predictive model, "),
                            React.createElement("span", null, `data readiness > 0 means the model is better than random chance. The readiness is adjusted for skewed data. Any score above ${AI_READINESS_BREAKPOINT.NO_SIGNAL * 10} is potentially useful, above ${AI_READINESS_BREAKPOINT.LOW_SIGNAL * 10} is likely useful and above ${AI_READINESS_BREAKPOINT.HIGH_SIGNAL * 10} may be too good to be true.`)),
                        React.createElement("div", { className: styles.aiReadinessBody }, "The readiness is adjusted for skewed data."))) },
                React.createElement("div", { className: styles.aireadinessIcon + " " + styles.isInfo },
                    React.createElement(FontAwesomeIcon, { icon: faInfo }))))));
};
function choiceToPending(choice) {
    return {
        type: choice.type,
        column: choice.variable,
        expr: choiceToExpression(choice),
        alias: choiceToAlias(choice)
    };
}
function choiceToExpression(choice) {
    const c = col(choice.variable);
    switch (choice.type) {
        case "ReciprocalTransform":
            return ifThen().if(c.ne(0), scalar(1).divide(c)).else(null);
        case "LogTransform":
            return ifThen().if(c.gt(0), c.apply("log")).else(null);
        case "SquareRootTransform":
            return ifThen().if(c.ge(0), c.apply("sqrt")).else(null);
        case "MonthlySeasonalityTransform":
            return c.extractDatePart(DatePart.MONTH);
        case "WeekdaySeasonalityTransform":
            return c.extractDatePart(DatePart.ISODOW);
        case "TimeTrendTransform":
            return c.extractDatePart(DatePart.EPOCH);
        default:
            throw Error(`unsupported suggested feature type: ${choice.type}`);
    }
}
function choiceToAlias(choice) {
    return `${choice.variable}_${choice.type}`;
}
const AutoTransformation = ({ dataset, ignored, updateIgnored, pendingTransformations, updatePendingTransformations }) => {
    return (React.createElement("div", null,
        React.createElement("div", null, dataset &&
            dataset.suggested_features &&
            dataset.suggested_features.map((field, i) => {
                const transformChoices = field && field.choices && field.choices.filter((c) => TRANSFORM_TYPE_MAP[c.type] !== undefined);
                if (!transformChoices?.length) {
                    return React.createElement(React.Fragment, null);
                }
                const name = field && field.choices && field.choices.length > 0 ? field.choices[0].variable : "Field Name";
                const hasChoices = field &&
                    field.choices &&
                    field.choices
                        .filter((c) => TRANSFORM_TYPE_MAP[c.type] !== undefined)
                        .filter((c) => {
                        const isChecked = pendingTransformations.find(({ column, type }) => column === name && type === c.type);
                        return isChecked !== undefined;
                    }).length > 0;
                return (React.createElement("div", { key: i, style: { marginBottom: "20px" } },
                    React.createElement("div", null,
                        React.createElement("div", { className: styles.pFlex },
                            React.createElement("div", { className: styles.aCol, style: { fontWeight: "bold", flexGrow: 1 } }, name),
                            hasChoices && (React.createElement("div", { className: styles.bCol, style: {
                                    minWidth: "90px",
                                    fontWeight: "bold",
                                    textAlign: "center"
                                } }, "Filter Original")),
                            hasChoices && (React.createElement("div", { className: styles.bCol, style: { fontWeight: "bold" } }, "New Field Label"))),
                        transformChoices.map((c, j) => {
                            const pending = pendingTransformations.find(({ column, type }) => column === name && type === c.type);
                            return (React.createElement("div", { className: styles.flexRow, style: { height: "35px" }, key: j },
                                React.createElement("input", { type: "checkbox", checked: pending !== undefined, onChange: () => {
                                        if (pending) {
                                            updatePendingTransformations(pendingTransformations.filter(({ column, type }) => !(column === name && type === c.type)));
                                            if (ignored[c.variable]) {
                                                updateIgnored(c.variable, false);
                                            }
                                        }
                                        else {
                                            updatePendingTransformations((field.choice_type == "AnyOf"
                                                ? pendingTransformations
                                                : pendingTransformations.filter(({ column }) => column != name)).concat([choiceToPending(c)]));
                                            if (!ignored[c.variable]) {
                                                updateIgnored(c.variable, true);
                                            }
                                        }
                                    }, style: {
                                        marginRight: "5px",
                                        marginTop: "-1px"
                                    } }),
                                React.createElement("div", { className: styles.derivedType }, TRANSFORM_TYPE_MAP[c.type] || c.type),
                                pending && (React.createElement("div", { className: styles.dRemove },
                                    React.createElement("input", { type: "checkbox", checked: ignored[c.variable], onClick: () => updateIgnored(c.variable, !ignored[c.variable]) }))),
                                pending && (React.createElement("div", { className: styles.derivedName },
                                    React.createElement(LineInput, { value: pending.alias, onChange: (val) => updatePendingTransformations(pendingTransformations.map((p) => !(p.column === name && p.type === c.type)
                                            ? p
                                            : {
                                                ...p,
                                                alias: val
                                            })) })))));
                        }))));
            }))));
};
export const UsefulVariables = ({ dataset, ignored, showAll, skipProblemPredictorsCheck }) => {
    const base = useSelector((state) => getBaseDraft(state, dataset.id));
    const [viewAllPredictors, updateViewAllPredictors] = useState(false);
    const aiReadyMetrics = useSelector((state) => getAiReadyMetrics(state, "new", dataset.id));
    const correlations = useSelector((state) => getCorrelationMetricsFromDataset(state, "new", dataset.id));
    const possiblePredictorsFirst = aiReadyMetrics?.aiReady || [];
    const possiblePredictorsSecond = aiReadyMetrics?.secondOrder?.aiReady || [];
    const possiblePredictors = [
        ...possiblePredictorsFirst,
        ...possiblePredictorsSecond.map((a) => ({
            ...a,
            label: a.label,
            firstLabel: a.keys?.[0],
            secondLabel: a.keys?.[1],
            isSecondOrder: true
        }))
    ].sort((a, b) => b.value - a.value);
    const problematicPredictors = correlations?.problematicPredictors;
    const isDone = dataset?.status_name === "DONE";
    const hasOutcome = dataset?.outcome !== undefined;
    return isDone !== true || hasOutcome !== true ? (React.createElement(React.Fragment, null)) : possiblePredictors && possiblePredictors.length > 0 ? (React.createElement("div", { style: { marginTop: "10px" } },
        possiblePredictors &&
            possiblePredictors
                .filter((p, i) => showAll || viewAllPredictors || i < 5)
                .map((p, i) => {
                const isIgnored = ignored &&
                    (ignored[p.label] === true ||
                        (p.firstLabel && ignored[p.firstLabel] === true) ||
                        (p.secondLabel && ignored[p.secondLabel] === true));
                const isProblematicPredictor = !skipProblemPredictorsCheck &&
                    Boolean(problematicPredictors?.find((prob) => prob?.label === p?.label || prob?.label === p?.firstLabel || prob?.label === p?.secondLabel));
                const valueLabel = p.value >= HIGH_CORRELATION_CUTOFF
                    ? "HIGH"
                    : (p.value * 10).toFixed(1) == "0.0" && p.value > 0
                        ? "<0.1"
                        : (p.value * 10).toFixed(1);
                return (React.createElement("div", { key: i, className: styles.pRow, style: {
                        opacity: isIgnored ? 0.3 : 1
                    } },
                    p.isSecondOrder === true && (React.createElement(OverlayTrigger, { placement: "top", trigger: ["hover"], rootClose: true, overlay: React.createElement(Popover, { id: "popover-positioned-top", title: "Variable Interaction Found" },
                            React.createElement("div", { className: styles.aiReadinessOverlay }, "Interactions are variable combinations where the impact on your outcome " +
                                "is significantly different than the two variables by themselves.")) },
                        React.createElement("div", { className: styles.pIcon },
                            React.createElement("img", { src: InteractIcon })))),
                    React.createElement("div", { className: styles.pLeft, style: p.isSecondOrder
                            ? {
                                minWidth: "calc(50% - 15px)",
                                marginLeft: "15px"
                            }
                            : {} },
                        React.createElement(OverlayTrigger, { placement: "top", trigger: ["hover"], rootClose: true, overlay: React.createElement(Popover, null, p.label) },
                            React.createElement("span", null, p.label))),
                    React.createElement("div", { className: styles.pRight },
                        React.createElement("div", { className: styles.outerBar },
                            React.createElement("div", { className: styles.innerBar, style: {
                                    width: Math.min(100, p.value * 100) + "%",
                                    background: isProblematicPredictor ? "#F8CD7E" : undefined,
                                    color: isProblematicPredictor ? "#4B495B" : undefined
                                } },
                                React.createElement("span", null, valueLabel))))));
            }),
        !showAll && possiblePredictors && possiblePredictors.length > 5 && (React.createElement("div", { className: styles.purpleA, onClick: () => updateViewAllPredictors(!viewAllPredictors), style: { fontSize: "13px" } }, viewAllPredictors ? "Hide" : "View all useful variables")))) : (React.createElement("div", null, "None"));
};
const OpenDataReadinessPanel = ({ dataset, ignored, updateIgnored }) => {
    const base = useSelector((state) => getBaseDraft(state, dataset.id));
    const baseContext = getOutputContext(base);
    const aiReadyMetrics = useSelector((state) => getAiReadyMetrics(state, "new", dataset.id));
    const correlations = useSelector((state) => getCorrelationMetricsFromDataset(state, "new", dataset.id));
    const possiblePredictorsFirst = aiReadyMetrics?.aiReady || [];
    const possiblePredictorsSecond = aiReadyMetrics?.secondOrder?.aiReady || [];
    const possiblePredictors = [
        ...possiblePredictorsFirst,
        ...possiblePredictorsSecond.map((a) => ({
            ...a,
            label: a.label,
            firstLabel: a.keys?.[0],
            secondLabel: a.keys?.[1],
            isSecondOrder: true
        }))
    ].sort((a, b) => b.value - a.value);
    const problematicPredictors = correlations?.problematicPredictors;
    const isDone = dataset?.status_name === "DONE";
    const hasOutcome = dataset?.outcome !== undefined;
    return (React.createElement("div", null,
        isDone && hasOutcome ? (React.createElement("div", { id: "problematicPredictorsSection_" + dataset?.id, style: { marginTop: "20px" } },
            React.createElement(ProblematicPredictors, { id: `data_${dataset?.id}`, isSense: true, selectedDataset: dataset, problematicPredictors: problematicPredictors.filter((f) => baseContext[f.label] !== undefined), ignored: ignored, predictors: correlations?.predictors.filter((f) => baseContext[f.label] !== undefined), updateIgnored: updateIgnored }))) : (React.createElement("div", { style: { marginTop: "20px" } },
            React.createElement("span", { className: styles.sIcon },
                React.createElement(FontAwesomeIcon, { icon: faCircle, style: { color: "rgb(200, 202, 212)" } })),
            React.createElement("span", { style: { fontWeight: "bold" } }, "Select an outcome to view Problematic Predictors"))),
        isDone && hasOutcome ? (React.createElement("div", { style: { marginTop: "20px" } },
            React.createElement("span", { className: styles.sIcon },
                React.createElement(FontAwesomeIcon, { icon: faCheckCircle })),
            React.createElement("span", { style: { fontWeight: "bold" } }, `${possiblePredictors?.length || 0} Possible Useful Variable${possiblePredictors?.length !== 1 ? "s" : ""} Found`))) : (React.createElement("div", { style: { marginTop: "20px" } },
            React.createElement("span", { className: styles.sIcon },
                React.createElement(FontAwesomeIcon, { icon: faCircle, style: { color: "rgb(200, 202, 212)" } })),
            React.createElement("span", { style: { fontWeight: "bold" } }, "Select an outcome to view Possible Useful Variables"))),
        React.createElement(UsefulVariables, { dataset: dataset })));
};
const DataReadinessPanel = ({ datasetId }) => {
    const dispatch = useDispatch();
    const dataset = useSelector((state) => getDataset(state, datasetId));
    const isOpen = useSelector((state) => getIsOpenAiReadiness(state, datasetId));
    const updateIsOpen = (isOpenAiReadiness) => {
        dispatch(updateTileState(datasetId, { isOpenAiReadiness }));
    };
    const [isTransformSaving, updateIsTransformSaving] = useState(false);
    const [isShowDerived, updateIsShowDerived] = useState(false);
    const [ignoredColumns, updateIgnoredColumns] = useState({});
    const [pendingTransformations, updatePendingTransformations] = useState([]);
    const updateIgnored = (c, isIgnored) => updateIgnoredColumns({
        ...ignoredColumns,
        ...(Array.isArray(c)
            ? c.reduce((update, c) => {
                update[c] = isIgnored;
                return update;
            }, {})
            : { [c]: isIgnored })
    });
    const base = useSelector((state) => getBaseDraft(state, datasetId));
    const aiReadyMetrics = useSelector((state) => getAiReadyMetrics(state, "new", datasetId));
    const predictors = [...(aiReadyMetrics?.aiReady ?? []), ...(aiReadyMetrics?.secondOrder?.aiReady ?? [])].sort((a, b) => b.value - a.value);
    const problematicPredictors = useSelector((state) => getCorrelationMetricsFromDataset(state, "new", datasetId).problematicPredictors);
    const aiScore = (aiReadyMetrics?.aiReadinessScore ?? 0) * 10;
    const aiStatus = aiReadyMetrics?.aiReadinessStatus;
    const isLowPredictors = predictors?.length <= AI_READINESS_PREDICTOR_WARNING.count;
    const isGray = aiStatus === "NO_SIGNAL";
    const hasInteractionScores = dataset?.interaction_model_metrics !== undefined && isNaN(aiScore) !== true;
    const aiReadinessMessage = hasInteractionScores !== true ? "Data Readiness not available" : getAiReadinessMessage(dataset, aiReadyMetrics);
    const dCount = dataset?.suggested_features_count ?? 0;
    const isWarning = hasInteractionScores !== true || aiStatus === "LOW_SIGNAL" || aiStatus === "SUSPECT_SIGNAL";
    return (React.createElement("div", { id: "dataReadinessSection_" + dataset?.id, className: isOpen ? styles.openOverview : styles.overview, style: {
            maxHeight: isOpen ? undefined : "190px",
            width: "100%",
            overflow: isOpen ? "auto" : "hidden"
        }, onClick: () => !isOpen && updateIsOpen(true) },
        React.createElement("div", { className: styles.senseTitle },
            React.createElement("div", { className: styles.sTitle },
                React.createElement("div", { className: styles.sIcon, style: isWarning || isGray ? { color: "#F8CD7E" } : {} },
                    React.createElement(FontAwesomeIcon, { icon: isWarning || isGray ? faExclamationTriangle : faCheckCircle })),
                isOpen ? aiReadinessMessage : aiReadinessMessage?.split(". ")[0]),
            isOpen ? (React.createElement("div", { id: "dataReadinessSectionClose_" + dataset?.id, className: styles.closeButton, onClick: () => updateIsOpen(false) },
                React.createElement(Button, { style: { padding: "3px" } },
                    React.createElement("div", { className: styles.selIcon, title: "Close" },
                        React.createElement(FontAwesomeIcon, { icon: faCompressArrowsAlt }))))) : (React.createElement("div", { className: styles.selIcon + " " + styles.expandIcon },
                React.createElement(FontAwesomeIcon, { icon: faExpandArrowsAlt })))),
        React.createElement("div", null,
            React.createElement("div", { className: styles.overviewGroup, style: {
                    marginTop: "0px",
                    alignItems: "flex-start",
                    width: "100%"
                } },
                React.createElement("div", { className: styles.overviewItem, style: { width: "130px", marginTop: "20px", minHeight: "125px" } },
                    React.createElement("div", { className: styles.overviewTop },
                        React.createElement(DataReadinessTooltip, { dataset: dataset })),
                    hasInteractionScores === true && (React.createElement("div", { className: styles.muscleChartSmall, style: { height: "110px", pointerEvents: "none" } },
                        React.createElement(SensePieChart, { score: aiScore, status: aiStatus, barColor: isGray ? "#F8CD7E" : undefined, isLarge: true }))),
                    React.createElement("div", { className: styles.overviewLargeCenter }, hasInteractionScores !== true ? (dataset?.status_name !== "ERROR" && dataset?.status_name !== "DONE" ? (React.createElement("div", { className: styles.noInteractions },
                        React.createElement(LoadingIndicator, { style: { margin: "0px 0px 0px 40px" } }))) : (React.createElement("div", { className: styles.noInteractions }, "Data Readiness is not available. Please try using a " +
                        "dataset with less columns/rows, or try uploading the dataset to Spark."))) : aiScore.toFixed(1) === "10.0" ? (10) : (aiScore.toFixed(1)))),
                React.createElement("div", { id: "usefulVariablesSection_" + dataset?.id, className: styles.overviewItem, style: { marginTop: "20px", minHeight: "125px" } },
                    React.createElement("div", { className: styles.overviewTop }, "USEFUL VARIABLES FOUND"),
                    React.createElement("div", { className: styles.overviewLarge + " " + styles.hFlex, style: { minHeight: "105px" } },
                        React.createElement("div", { className: styles.muscleChartSmall },
                            React.createElement("span", { style: { marginRight: isLowPredictors ? "6px" : undefined } }, predictors ? predictors.length : "..."),
                            isLowPredictors && (React.createElement(Tooltip, { trigger: ["hover"], overlay: React.createElement("div", { style: { maxWidth: "250px" } }, `There are less than 6 useful variables found for ${dataset?.outcome ?? "the outcome"}. Adding more data may increase this number and improve the quality of the insights and AI models.`) },
                                React.createElement(Icon, { type: "warningYellow" })))),
                        predictors ? (React.createElement("div", { className: styles.predictorChart },
                            predictors.map((p, i) => {
                                if ((!isOpen && i >= 15) || i > MAX_USEFUL_VARIABLES) {
                                    return React.createElement(React.Fragment, { key: i });
                                }
                                const barHeight = isOpen
                                    ? Math.ceil(50 / predictors.length)
                                    : Math.max(3, Math.ceil(50 / predictors.length));
                                const isProblematicPredictor = Boolean(problematicPredictors?.find((prob) => prob?.label === p?.label || prob?.label === p?.keys?.[0] || prob?.label === p?.keys?.[1]));
                                return (React.createElement("div", { className: styles.predictorBar, style: {
                                        width: p.value * 100 + "%",
                                        height: barHeight,
                                        background: isProblematicPredictor ? "#F8CD7E" : undefined
                                    }, title: p.label, key: "bar_" + i }));
                            }),
                            !isOpen && predictors.length > 15 && (React.createElement("div", { className: styles.predictorOverflowLabel }, "Show all " + predictors?.length)),
                            isOpen && predictors.length > MAX_USEFUL_VARIABLES && (React.createElement("div", { className: styles.predictorOverflowLabel }, predictors?.length - MAX_USEFUL_VARIABLES + " Other Variables")))) : (React.createElement("div", null)))))),
        isOpen && React.createElement(OpenDataReadinessPanel, { dataset: dataset, ignored: ignoredColumns, updateIgnored: updateIgnored }),
        (!isEnabled("chat-aible-consumer") || isEnabled("aible-optimize")) && isOpen && (React.createElement("div", { className: styles.suggestedFields },
            React.createElement("div", { className: styles.suggestedFieldsHeader },
                React.createElement("span", { className: styles.sIcon },
                    React.createElement("img", { src: BellIcon, style: { height: "20px", width: "20px" } })),
                dCount + " Aible Recommended Derived Fields"),
            React.createElement("div", { style: { marginTop: "10px", marginBottom: "10px" }, className: styles.purpleA, onClick: () => updateIsShowDerived(!isShowDerived) }, isShowDerived ? "Collapse All Recommended Fields" : "View All Recommended Fields"),
            isShowDerived && (React.createElement("div", { className: styles.autoDerivedList },
                React.createElement(AutoTransformation, { dataset: dataset, ignored: ignoredColumns, updateIgnored: updateIgnored, pendingTransformations: pendingTransformations, updatePendingTransformations: updatePendingTransformations }))))),
        isOpen ? React.createElement(ColumnIgnoredWarning, { dataset: dataset }) : React.createElement(React.Fragment, null),
        isOpen ? (React.createElement("div", { style: {
                display: "flex",
                justifyContent: "center",
                width: "100%"
            } }, isTransformSaving ? (React.createElement(LoadingIndicator, { style: { margin: "0px" } })) : (React.createElement(Button, { type: "primary", onClick: () => {
                updateIsTransformSaving(true);
                let r = builder(base);
                if (pendingTransformations.length > 0) {
                    r = r.mutate(pendingTransformations.map((p) => p.expr.buildAsProjection(p.alias)));
                }
                if (ignoredColumns) {
                    r = r.exclude(Object.keys(ignoredColumns).filter((c) => ignoredColumns[c]));
                }
                dispatch(rebaseDraft(datasetId, r.build()));
                dispatch(submitDraft(datasetId)).then(() => updateIsTransformSaving(false));
            }, disabled: ignoredColumns &&
                Object.keys(ignoredColumns).filter((k) => ignoredColumns[k] === true).length === 0 &&
                pendingTransformations.length === 0 }, "Apply and Refresh Analysis")))) : (React.createElement("div", null))));
};
export default DataReadinessPanel;
